import React, { useCallback, useMemo, useState } from "react";
import Link from "next/link";
import { IconButton, Paper, Tooltip } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Edit as EditIcon } from "@material-ui/icons";
import { DataTable } from "src/apps/commons/dataTable";
import { formatDateTime } from "src/utils";
import { ActiveStatus } from "../../../commons/dataDisplays";
import { ImageAvatar } from "../../../commons/fileUploads";
import * as Api from "../api";
import {
  IRemyndWarrantyBrief,
  IRemyndWarrantyPaginateReq,
  RemyndWarrantyPaginateOptionKey
} from "../types";
import { constants, paths } from "../utils";
import ListFilterDialog from "./ListFilterDialog";
import SearchWarranty from "./SearchWarranty";

const useStyles = makeStyles(theme => ({
  root: {
    width: "100%",
    marginTop: theme.spacing(2),
    backgroundColor: theme.palette.background.default
  }
}));

const ListContainers: React.FC<unknown> = _ => {
  const itemToString = useCallback(
    (data: IRemyndWarrantyBrief) => `${data.titleTh}`,
    []
  );

  const classes = useStyles();
  const [refreshDataKey, setRefreshDataKey] = useState(0);
  const onDeleteSuccess = useCallback(
    () => setRefreshDataKey(Math.random()),
    []
  );

  const ManageCell = useCallback(
    (data: IRemyndWarrantyBrief, isLoading: boolean) => (
      <div>
        {isLoading ? (
          <IconButton
            aria-label="Edit"
            key={`loading_manage_${data.id}`}
            disabled
          >
            <EditIcon />
          </IconButton>
        ) : (
          <Link href={`${paths.edit}?id=${data.id}`} key={`manage_${data.id}`}>
            <a>
              <IconButton aria-label="Edit">
                <Tooltip title={`แก้ไข ${constants.label}`} placement="top">
                  <EditIcon />
                </Tooltip>
              </IconButton>
            </a>
          </Link>
        )}
      </div>
    ),
    []
  );
  const ActiveCell = useCallback(
    (data: IRemyndWarrantyBrief) => <ActiveStatus value={data.active} />,
    []
  );
  const ImageCell = useCallback((data: IRemyndWarrantyBrief) => {
    return data.imageId ? (
      <ImageAvatar fileId={data.imageId} fit="contain" />
    ) : (
      <div />
    );
  }, []);

  const defaultCriteria = useMemo(
    () =>
      ({
        paginate: {
          page: 1,
          pageSize: 10,
          order: { optionKey: "UPDATED_AT", desc: true }
        }
      } as IRemyndWarrantyPaginateReq),
    []
  );

  return (
    <>
      <Paper className={classes.root}>
        <DataTable<
          IRemyndWarrantyBrief,
          IRemyndWarrantyPaginateReq,
          RemyndWarrantyPaginateOptionKey,
          "MANAGE" | "COUNT_BRAND" | "COUNT_PRODUCT" | "IMAGE"
        >
          pathCreate={paths.create}
          searchTermLabel={"ค้นหาจาก ชื่อ (TH/EN)"}
          listEntity={Api.list}
          deleteEntities={Api.deleteMultiple}
          itemToString={itemToString}
          onDeleteSuccess={onDeleteSuccess}
          filterDialog={ListFilterDialog}
          defaultCriteria={defaultCriteria}
          columns={[
            {
              id: "MANAGE",
              label: "จัดการ",
              width: 60,
              style: { paddingLeft: 0, paddingRight: 8 },
              align: "center",
              renderCell: ManageCell
            },
            {
              id: "IMAGE",
              label: "รูป",
              width: 80,
              align: "center",
              renderCell: ImageCell
            },
            {
              id: "TITLE_TH",
              label: "ชื่อ (TH)",
              minWidth: 250,
              orderable: true,
              renderCell: data => data.titleTh
            },
            {
              id: "TITLE_EN",
              label: "ชื่อ (EN)",
              minWidth: 250,
              orderable: true,
              renderCell: data => data.titleEn
            },
            {
              id: "COUNT_BRAND",
              label: "จำนวน\nแบรนด์",
              width: 80,
              align: "center",
              renderCell: data => `${data.countBrands}`,
              renderFaded: data => !data.countBrands
            },
            {
              id: "COUNT_PRODUCT",
              label: "จำนวน\nสินค้า",
              width: 80,
              align: "center",
              renderCell: data => `${data.countProducts}`,
              renderFaded: data => !data.countProducts
            },
            {
              id: "ACTIVE",
              label: "Active",
              width: 80,
              orderable: true,
              align: "center",
              renderCell: ActiveCell
            },
            {
              id: "CREATED_AT",
              label: "สร้างเมื่อ",
              width: 200,
              orderable: true,
              renderCell: data => formatDateTime(data.createdAt)
            },
            {
              id: "UPDATED_AT",
              label: "แก้ไขล่าสุดเมื่อ",
              width: 200,
              orderable: true,
              renderCell: data => formatDateTime(data.updatedAt)
            }
          ]}
        />
      </Paper>

      <SearchWarranty refreshDataKey={refreshDataKey} />
    </>
  );
};

export default ListContainers;
